import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import PdfUpload from "@/components/PdfUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import ResourceGallery from "@/components/ResourceGallery";
import { Folder, Download, ExternalLink, Plus, X, Clock, CheckCircle2, XCircle, FileText, Video, ClipboardList, Library, Sparkles, BookOpen } from "lucide-react";

const TYPES = [
  { id: "document", label: "Document", icon: FileText },
  { id: "video", label: "Vidéo", icon: Video },
  { id: "template", label: "Template", icon: ClipboardList },
  { id: "study", label: "Étude", icon: Library },
  { id: "toolkit", label: "Boîte à outils", icon: Sparkles },
];

const CATEGORIES = ["Marketing", "Légal", "Tech", "Vente", "RH", "Finance", "Production", "Innovation", "Général"];

function emptyForm() {
  return { title: "", description: "", category: "Général", type: "document", pdf_url: "", external_url: "", cover_image: "", access: "member", images: [] };
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "En attente", icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200" },
    published: { label: "Publiée", icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    rejected: { label: "Rejetée", icon: XCircle, cls: "bg-rose-50 text-rose-700 border-rose-200" },
  };
  const m = map[status] || map.pending;
  const I = m.icon;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${m.cls}`} data-testid={`resource-status-${status}`}><I className="h-2.5 w-2.5" />{m.label}</span>;
}

export default function MemberResources() {
  const [tab, setTab] = useState("browse");
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get("/resources").then((r) => setItems((r.data || []).filter((x) => x.access !== "press"))).catch(() => {});
    api.get("/portal/resources/my").then((r) => setMine(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pdf_url && !form.external_url && !form.images?.length) {
      toast.error("Ajoutez un PDF, des images ou une URL externe");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/portal/resources/submit", form);
      toast.success("Ressource soumise — l'admin va l'examiner sous peu");
      setForm(emptyForm());
      setOpen(false);
      load();
      setTab("mine");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la soumission");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalLayout sidebar="member">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">Ressources</h1>
          <p className="mt-1 text-neutral-600">Guides, rapports, templates exclusifs membres. Partagez les vôtres et gagnez <strong>+8 points</strong> par contribution validée.</p>
        </div>
        <button onClick={() => { setOpen(true); setTab("browse"); }} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="resource-submit-btn">
          <Plus className="h-4 w-4" />Proposer une ressource
        </button>
      </div>

      <div className="mt-6 inline-flex gap-1 rounded-full bg-neutral-100 p-1">
        <button onClick={() => setTab("browse")} className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "browse" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="resources-tab-browse">
          <BookOpen className="mr-1 inline h-3.5 w-3.5" />Toutes les ressources ({items.length})
        </button>
        <button onClick={() => setTab("mine")} className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "mine" ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid="resources-tab-mine">
          Mes soumissions {mine.length > 0 && <span className="ml-1 rounded-full bg-wine-100 px-1.5 py-0.5 text-[10px] text-wine-700">{mine.length}</span>}
        </button>
      </div>

      {tab === "browse" && (
        <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.length === 0 && <div className="md:col-span-3 rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="resources-empty">Aucune ressource pour le moment. Soyez le premier à en proposer une !</div>}
          {items.map((r) => {
            const T = TYPES.find((t) => t.id === r.type) || TYPES[0];
            const Icon = T.icon;
            return (
              <div key={r.id} className="overflow-hidden rounded-2xl border border-neutral-100 bg-white" data-testid={`resource-${r.id}`}>
                {r.cover_image && <img src={r.cover_image} alt={r.title} className="aspect-[16/9] w-full object-cover" />}
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-wine-700"><Icon className="h-3 w-3" />{T.label} · {r.category}</div>
                  <h3 className="mt-2 font-serif text-base font-semibold text-neutral-900">{r.title}</h3>
                  <p className="mt-1 line-clamp-3 text-sm text-neutral-600">{r.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-wine-50 px-3 py-1 text-xs font-semibold text-wine-700 hover:bg-wine-100" data-testid={`resource-dl-${r.id}`}><Download className="h-3 w-3" />Télécharger PDF</a>}
                    {r.external_url && <a href={r.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 px-3 py-1 text-xs font-semibold text-wine-700 hover:bg-wine-50" data-testid={`resource-ext-${r.id}`}><ExternalLink className="h-3 w-3" />Ouvrir le lien</a>}
                  </div>
                  <ResourceGallery resource={r} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "mine" && (
        <div className="mt-6 space-y-3">
          {mine.length === 0 && <div className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500" data-testid="mine-empty">Vous n&apos;avez encore soumis aucune ressource.</div>}
          {mine.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-100 bg-white p-4" data-testid={`mine-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-navy">{r.title}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-xs text-neutral-500">{r.category} · {r.type} · soumise le {new Date(r.created_at).toLocaleDateString("fr-FR")}</div>
                {r.status === "rejected" && r.rejected_reason && <div className="mt-1 text-xs text-rose-600">Raison du rejet : {r.rejected_reason}</div>}
              </div>
              <div className="flex gap-2">
                {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-wine-700 hover:underline"><Download className="inline h-3 w-3" /> PDF</a>}
                {r.external_url && <a href={r.external_url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-wine-700 hover:underline"><ExternalLink className="inline h-3 w-3" /> Lien</a>}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="resource-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy"><Folder className="mr-1 inline h-5 w-5 text-wine-500" />Proposer une ressource</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 text-xs text-neutral-500">Votre soumission sera examinée par un admin. Une fois validée, vous gagnez <strong>+8 points</strong>.</p>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="resource-title" />
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (à quoi sert cette ressource ?)" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="resource-description" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="resource-type">
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="resource-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="md:col-span-2">
                <PdfUpload value={form.pdf_url} onChange={({ url }) => setForm({ ...form, pdf_url: url })} label="Fichier PDF (optionnel)" testId="resource-pdf-upload" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Images téléchargeables (PNG / JPG, optionnel)</label>
                <MultiImageUpload value={form.images || []} onChange={(images) => setForm((f) => ({ ...f, images }))} testId="resource-images" />
              </div>
              <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="OU lien externe (Notion, Drive, YouTube, etc.)" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="resource-external-url" />
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Image de couverture (optionnel)</label>
                <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="" testId="resource-cover" />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" disabled={submitting} className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50" data-testid="resource-submit-confirm">
                {submitting ? "Envoi..." : "Soumettre pour validation"}
              </button>
            </div>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
